/** @jsxImportSource @emotion/react */
import { ExportOutlined } from '@ant-design/icons'
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import usePaymentProviderConnection, {
  AccountDetails,
  PaymentProviderId,
  PaymentProviderStatus,
  ProviderError,
  SportsPayAccountDetails,
} from '@sportninja/common/hooks/usePaymentProviderConnection'
import { Alert, Badge, Spin } from 'antd'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import Icon from 'src/components/Icon'
import Text from 'src/components/ts-components/Text'
import { UserData } from '..'
import { PAYMENT_PROVIDERS } from 'src/hooks/usePaymentProvider'
import { Account } from 'src/state/me'
import { getErrorMessage as getErrorMessageFromError } from '@sportninja/common/utils/utils'

// Country IDs for SportsPay support
const COUNTRY_ID_CANADA = 36
const COUNTRY_ID_USA = 226
const SUPPORTED_SPORTSPAY_COUNTRY_IDS = [COUNTRY_ID_CANADA, COUNTRY_ID_USA]

type PaymentProviderProps = {
  id: string
  name: string
  description: string
  logo: React.ReactNode
  isRecommended?: boolean
  hasCompetitiveRates?: boolean
  connectUrl?: string
  contactUrl?: string
  isConnected?: boolean
  accountDetails?: AccountDetails | SportsPayAccountDetails | null
  accountStatus?: PaymentProviderStatus | null
  accountNumber?: string
  shouldDisplayConnectButton?: boolean
}

const AccountDetailsStatusRow = ({
  status = 'success',
  label,
}: {
  status: 'success' | 'error'
  label: string
}) => {
  return (
    <div
      css={css`
        display: flex;
        gap: 8px;
        justify-content: flex-start;
        align-items: center;
      `}
    >
      <Badge status={status} />
      <Text variant='paragraphSmall' color={colors.WHITE} weight='regular'>
        {label}
      </Text>
    </div>
  )
}

/**
 * Get URL parameters from the current location
 * @returns Object with URL parameters
 */
const getUrlParams = () => {
  const searchParams = new URLSearchParams(window.location.search)
  return {
    origin: searchParams.get('origin'),
    data: searchParams.get('data'),
    code: searchParams.get('code'),
    status: searchParams.get('status'),
    onboarding_sportspay: searchParams.get('onboarding_sportspay'),
    onboarding_stripe: searchParams.get('onboarding_stripe'),
  }
}

const PaymentProviders = ({
  user,
  paymentProviderId,
  account,
}: {
  user: UserData
  paymentProviderId: string | null
  account: Account
}) => {
  const stripeOnboarding = useRef(false)
  const [updateAccountStatusError, setUpdateAccountStatusError] = useState<
    string | null
  >(null)
  const [showOtherProviders, setShowOtherProviders] = useState(false)
  const urlParameters = getUrlParams()
  const {
    loading,
    link,
    isOnboarded,
    accountDetails,
    error,
    errorDetails,
    fetchPaymentProvidersData,
    resetState,
    accountStatus,
    putAccountInfo,
    putAccountInfoForStripeOnboarding,
    fetchPaymentProviderUrl,
  } = usePaymentProviderConnection()

  const lAccountStatus = useMemo(() => {
    if (
      accountStatus === PaymentProviderStatus.INCOMPLETE &&
      urlParameters?.status === PaymentProviderStatus.IN_REVIEW
    ) {
      return PaymentProviderStatus.IN_REVIEW
    }
    return accountStatus || urlParameters?.status
  }, [accountStatus, urlParameters?.status])
  const shouldDisplayConnectButton =
    lAccountStatus !== PaymentProviderStatus.COMPLETED &&
    lAccountStatus !== PaymentProviderStatus.IN_REVIEW

  const isSportsPaySupported = useMemo(() => {
    const countryId = account?.address?.country?.id
    if (!countryId) {
      return false
    }
    return SUPPORTED_SPORTSPAY_COUNTRY_IDS.includes(countryId)
  }, [account?.address?.country?.id])

  const shouldDisplayOtherProviders = useMemo(() => {
    if (urlParameters?.code && urlParameters?.status) {
      return false
    }
    if (paymentProviderId) {
      return false
    }
    if (!isSportsPaySupported) {
      return false
    }
    return true
  }, [urlParameters, paymentProviderId, isSportsPaySupported])

  const accountNumber = accountDetails?.account || ''

  useEffect(() => {
    if (user.account_id) {
      fetchPaymentProvidersData(user.account_id)
    }
    return () => {
      resetState()
    }
  }, [user.account_id, resetState, fetchPaymentProvidersData])

  useEffect(() => {
    const updateAccountStatus = async () => {
      try {
        let shouldUpdateAccountStatus = false
        // If the existing status is incomplete and the new status is in review, update the account status
        if (
          urlParameters?.code &&
          urlParameters?.status &&
          accountStatus === PaymentProviderStatus.INCOMPLETE &&
          urlParameters?.status === PaymentProviderStatus.IN_REVIEW
        ) {
          shouldUpdateAccountStatus = true
        }
        // If there is no payment provider id and the url parameters are present, update the account status
        if (
          urlParameters?.code &&
          urlParameters?.status &&
          !paymentProviderId
        ) {
          shouldUpdateAccountStatus = true
        }
        if (shouldUpdateAccountStatus) {
          putAccountInfo(
            urlParameters.code as string,
            urlParameters.status as PaymentProviderStatus,
            user.account_id,
            '2' as PaymentProviderId
          )
        }
      } catch (e) {
        const errorMessage = getErrorMessageFromError(e)
        setUpdateAccountStatusError(errorMessage)
      }
    }
    updateAccountStatus()
  }, [
    urlParameters,
    putAccountInfo,
    user.account_id,
    paymentProviderId,
    accountStatus,
  ])

  useEffect(() => {
    const updateAccountStatusForStripeOnboarding = async () => {
      try {
        let shouldUpdateAccountStatus = false
        // if the origin is onboarding_stripe, update the account status
        if (urlParameters?.origin?.includes('onboarding_stripe')) {
          console.log(`Found origin: ${urlParameters?.origin}`)
          shouldUpdateAccountStatus = true
        }
        if (shouldUpdateAccountStatus) {
          if (stripeOnboarding.current) {
            console.log('Stripe onboarding already in progress')
            return
          }
          if (!user?.account_id) {
            console.log('No account id found')
            return
          }
          // If we already have a payment provider id, don't update the account status
          if (paymentProviderId) {
            console.log(
              'Payment provider id found, we should not update the account status'
            )
            return
          }
          // If we already have a payment provider id under the account,
          // don't update the account status
          if (account?.payment_provider_type?.id) {
            console.log(
              'Payment provider id found under the account, we should not update the account status'
            )
            return
          }
          await putAccountInfoForStripeOnboarding(
            user?.account_id,
            // 1 is STRIPE
            '1' as PaymentProviderId
          )
          await fetchPaymentProvidersData(user?.account_id)
          stripeOnboarding.current = true
          // Add origin=onboarding to URL before reloading
          window.history.replaceState(
            {},
            document.title,
            `${window.location.pathname}?origin=onboarding`
          )
          window.location.reload()
        }
      } catch (e) {
        const errorMessage = getErrorMessageFromError(e)
        setUpdateAccountStatusError(errorMessage)
      }
    }
    updateAccountStatusForStripeOnboarding()
  }, [
    urlParameters,
    putAccountInfoForStripeOnboarding,
    user?.account_id,
    fetchPaymentProvidersData,
  ])

  const sportsPay: PaymentProviderProps = {
    id: 'sportspay',
    name: 'SportsPay',
    description:
      'An Easy, Integrated Payment Solution That Guarantees Users And Administrators Competitive Rates And A Simple Registration Experience.',
    logo: (
      <img
        src='/images/sportspay.png'
        alt='SportsPay'
        css={css`
          width: 32px;
          height: 32px;
          border-radius: 4px;
        `}
      />
    ),
    isRecommended: true,
    hasCompetitiveRates: true,
    connectUrl: link ? `${link}` : '',
    isConnected: isOnboarded,
    accountDetails: accountDetails,
    accountStatus: accountStatus,
    accountNumber: accountNumber as string,
    shouldDisplayConnectButton: shouldDisplayConnectButton,
  }

  const stripe: PaymentProviderProps = {
    id: 'stripe',
    name: 'Stripe',
    description:
      'Accept Payments Online And In Person And Build A More Profitable Business.',
    logo: (
      <img
        src='/images/stripe.png'
        alt='Stripe'
        css={css`
          width: 32px;
          height: 32px;
        `}
      />
    ),
    connectUrl: link || '',
    isConnected: isOnboarded,
    accountDetails: accountDetails,
    shouldDisplayConnectButton: shouldDisplayConnectButton,
  }

  const getErrorMessage = (providerError: ProviderError) => {
    if (!providerError) {
      return 'There Was An Error Connecting To The Payment Provider.'
    }
    return (
      providerError.message ||
      'There Was An Error Connecting To The Payment Provider.'
    )
  }

  const getErrorDescription = (providerError: ProviderError) => {
    if (!providerError) {
      return 'Please Try Again Later Or Contact Support If The Issue Persists.'
    }
    if (providerError.code) {
      return `Error Code: ${providerError.code}. Please Try Again Later Or Contact Support If The Issue Persists.`
    }
    return 'Please Try Again Later Or Contact Support If The Issue Persists.'
  }

  const renderStatusBanner = () => {
    if (!lAccountStatus) {
      return null
    }
    switch (lAccountStatus) {
      case PaymentProviderStatus.ERROR:
        return (
          <Alert
            description='There Was An Error Connecting To Your Payment Provider. Please Try Again or Contact the Payment Provider Support.'
            type='error'
            showIcon
            css={css`
              padding: 8px;
              margin-top: 8px;
              background-color: rgba(255, 77, 79, 0.1);
              border: 1px solid ${colors.ERROR_100};

              .ant-alert-message {
                color: ${colors.WHITE};
                font-weight: 600;
              }

              .ant-alert-description {
                color: ${colors.WHITE};
              }

              .ant-alert-icon {
                color: ${colors.ERROR_100};
              }
            `}
          />
        )
      case PaymentProviderStatus.INCOMPLETE:
        return (
          <Alert
            description='You Still Need To Complete The Onboarding Process. Please Click On The Button Below To Continue.'
            type='info'
            showIcon
            css={css`
              padding: 8px;
              margin-top: 8px;
              background-color: rgba(90, 166, 255, 0.1);
              border: 1px solid ${colors.INFO_200};

              .ant-alert-message {
                color: ${colors.WHITE};
                font-weight: 600;
              }

              .ant-alert-description {
                color: ${colors.WHITE};
              }

              .ant-alert-icon {
                color: ${colors.INFO_200};
              }
            `}
          />
        )
      case PaymentProviderStatus.IN_REVIEW:
        return (
          <Alert
            description={
              'Your  Account Is Being Reviewed. You Will Receive An Email Once It Is Approved.'
            }
            type='info'
            showIcon
            css={css`
              padding: 8px;
              margin-top: 8px;
              background-color: rgba(90, 166, 255, 0.1);
              border: 1px solid ${colors.INFO_200};

              .ant-alert-message {
                color: ${colors.WHITE};
                font-weight: 600;
              }

              .ant-alert-description {
                color: ${colors.WHITE};
              }

              .ant-alert-icon {
                color: ${colors.INFO_200};
              }
            `}
          />
        )
      case PaymentProviderStatus.COMPLETED:
        return (
          <Alert
            description='Your Account Is Connected. You Can Now Start Accepting Payments.'
            type='success'
            showIcon
            css={css`
              padding: 8px;
              margin-top: 8px;
              background-color: rgba(102, 255, 153, 0.1);
              border: 1px solid ${colors.SUCCESS_200};
              .ant-alert-message {
                color: ${colors.WHITE};
                font-weight: 600;
              }
              .ant-alert-description {
                color: ${colors.WHITE};
              }
              .ant-alert-icon {
                color: ${colors.SUCCESS_200};
              }
            `}
          />
        )
      default:
        return null
    }
  }

  const renderErrorBanner = () => {
    if (!updateAccountStatusError) {
      return null
    }
    return (
      <Alert
        description={updateAccountStatusError}
        type='error'
        showIcon
        css={css`
          padding: 8px;
          margin-top: 8px;
          background-color: rgba(255, 77, 79, 0.1);
          border: 1px solid ${colors.ERROR_100};

          .ant-alert-message {
            color: ${colors.WHITE};
            font-weight: 600;
          }

          .ant-alert-description {
            color: ${colors.WHITE};
          }

          .ant-alert-icon {
            color: ${colors.ERROR_100};
          }
        `}
      />
    )
  }

  return (
    <div
      css={css`
        margin-top: 16px;
      `}
    >
      <div
        css={css`
          background-color: ${colors.SECONDARY_300};
          padding: 16px;
          border-radius: 8px 8px 0 0;
          border: 1px solid ${colors.SECONDARY_200};
          max-width: 1080px;
          margin: 0 auto;
        `}
      >
        <Text variant='heading4' weight='semiBold' color={colors.WHITE}>
          PAYMENT PROVIDER
        </Text>
        <Text
          variant='paragraphXSmall'
          weight='regular'
          color={colors.NEUTRAL_50}
          style='margin-top: 8px;'
        >
          Link Your Payment Processing Account To Manage Registration And
          Transactions.
        </Text>
      </div>

      <div
        css={css`
          max-width: 1080px;
          margin: 0 auto;
          padding: 16px;
          background-color: ${colors.SECONDARY_300};
          border-radius: 0 0 8px 8px;
          border: 1px solid ${colors.SECONDARY_200};
          gap: 16px;
          display: flex;
          flex-direction: column;
        `}
      >
        {renderErrorBanner()}
        {error && (
          <Alert
            message={getErrorMessage(errorDetails || ({} as ProviderError))}
            description={getErrorDescription(
              errorDetails || ({} as ProviderError)
            )}
            type='error'
            showIcon
            css={css`
              margin-bottom: 16px;
              background-color: rgba(255, 77, 79, 0.1);
              border: 1px solid ${colors.ERROR_100};

              .ant-alert-message {
                color: ${colors.WHITE};
                font-weight: 600;
              }

              .ant-alert-description {
                color: ${colors.NEUTRAL_50};
              }

              .ant-alert-icon {
                color: ${colors.ERROR_100};
              }
            `}
            action={
              <button
                css={css`
                  height: 32px;
                  display: flex;
                  padding: 8px 16px;
                  justify-content: center;
                  align-items: center;
                  gap: 8px;
                  border-radius: 4px;
                  background-color: ${colors.ERROR_100};
                  border: none;
                  cursor: pointer;
                  color: white;
                  font-weight: 500;

                  &:hover {
                    background-color: #ff7875;
                  }
                `}
                onClick={() => fetchPaymentProvidersData(user.account_id)}
              >
                Retry
              </button>
            }
          />
        )}
        {error && (
          <Alert
            message={getErrorMessage(errorDetails || ({} as ProviderError))}
            description={getErrorDescription(
              errorDetails || ({} as ProviderError)
            )}
            type='error'
            css={css`
              margin-bottom: 16px;
              background-color: rgba(255, 77, 79, 0.1);
              border: 1px solid ${colors.ERROR_100};

              .ant-alert-message {
                color: ${colors.WHITE};
                font-weight: 600;
              }

              .ant-alert-description {
                color: ${colors.NEUTRAL_50};
              }

              .ant-alert-icon {
                color: ${colors.ERROR_100};
              }
            `}
            action={
              <button
                css={css`
                  height: 32px;
                  display: flex;
                  padding: 8px 16px;
                  justify-content: center;
                  align-items: center;
                  gap: 8px;
                  border-radius: 4px;
                  background-color: ${colors.ERROR_100};
                  border: none;
                  cursor: pointer;
                  color: white;
                  font-weight: 500;

                  &:hover {
                    background-color: #ff7875;
                  }
                `}
                onClick={() => fetchPaymentProvidersData(user.account_id)}
              >
                Retry
              </button>
            }
          />
        )}
        {loading ? (
          <div
            css={css`
              display: flex;
              justify-content: center;
              padding: 16px;
            `}
          >
            <Spin
              size='small'
              css={css`
                .ant-spin-text {
                  color: ${colors.WHITE};
                  margin-top: 8px;
                }
              `}
            />
          </div>
        ) : (
          <>
            {renderStatusBanner()}
            <PaymentProviderCard
              provider={
                paymentProviderId === PAYMENT_PROVIDERS.STRIPE ||
                !isSportsPaySupported
                  ? stripe
                  : sportsPay
              }
              account={account}
              fetchPaymentProviderUrl={fetchPaymentProviderUrl}
            />
          </>
        )}

        <div
          css={css`
            display: flex;
            align-items: center;
            cursor: pointer;
            user-select: none;
            gap: 8px;
            /* if paymentProviderId is not null, then show the other providers */
            ${!shouldDisplayOtherProviders &&
            css`
              display: none;
            `}
          `}
          onClick={() => setShowOtherProviders(!showOtherProviders)}
        >
          <Icon
            name={showOtherProviders ? 'chevron-up' : 'chevron-down'}
            fontSize={16}
          />
          <Text variant='captionSmall' weight='semiBold' color={colors.WHITE}>
            OTHER PROVIDERS
          </Text>
        </div>

        {showOtherProviders &&
          isSportsPaySupported &&
          (loading ? (
            <div
              css={css`
                display: flex;
                justify-content: center;
                padding: 16px;
              `}
            >
              <Spin
                size='small'
                tip='Loading Payment Provider Information...'
                css={css`
                  .ant-spin-text {
                    color: ${colors.WHITE};
                    margin-top: 8px;
                  }
                `}
              />
            </div>
          ) : (
            <PaymentProviderCard
              provider={stripe}
              account={account}
              fetchPaymentProviderUrl={fetchPaymentProviderUrl}
            />
          ))}
      </div>
    </div>
  )
}

const AccountInfoRow = ({
  label,
  value,
}: {
  label: string
  value: string
  capitalize?: boolean
  uppercase?: boolean
}) => {
  if (value === '') {
    return null
  }

  return (
    <div
      css={css`
        display: flex;
        gap: 4px;
      `}
    >
      <Text variant='paragraphSmall' weight='bold' color={colors.NEUTRAL_0}>
        {label}:
      </Text>
      <Text variant='paragraphSmall' color={colors.WHITE} weight='regular'>
        {value}
      </Text>
    </div>
  )
}

const PaymentProviderCard = ({
  provider,
  account,
  fetchPaymentProviderUrl = () => {},
}: {
  provider: PaymentProviderProps
  account: Account
  fetchPaymentProviderUrl?: any
}) => {
  const isSportsPay = useMemo(
    () => provider.id === 'sportspay' && provider?.accountDetails?.account,
    [provider]
  )

  const isCanada = useMemo(() => {
    return account?.address?.country?.id === COUNTRY_ID_CANADA
  }, [account?.address?.country?.id])

  if (provider.isConnected && provider.accountDetails) {
    // Extract business profile information based on provider type
    const businessInfo = isSportsPay
      ? account
      : typeof provider.accountDetails.account === 'object'
      ? provider.accountDetails.account?.business_profile
      : undefined

    return (
      <div
        css={css`
          padding: 16px;
          background-color: ${colors.SECONDARY_300};
          border: 1px solid ${colors.SECONDARY_200};
          border-radius: 8px;
          display: flex;
          flex-direction: column;
          gap: 16px;
        `}
      >
        <div
          css={css`
            display: flex;
            align-items: center;
            justify-content: space-between;
          `}
        >
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: 12px;
            `}
          >
            {provider.logo}
            <Text variant='heading5' weight='regular' color={colors.WHITE}>
              {provider.name}
            </Text>
          </div>
          <a
            css={css`
              display: flex;
              gap: 6px;
              align-items: center;
            `}
            href={
              isSportsPay
                ? 'https://phq.sportspay.com'
                : 'https://dashboard.stripe.com/'
            }
            target='_blank'
            rel='noreferrer'
          >
            <p
              css={css`
                font-size: 14px;
                color: ${colors.DEFAULT_FLAIR};
              `}
            >
              Go to {provider.name} Dashboard
            </p>
            <Icon name='external-link-alt' color={colors.DEFAULT_FLAIR} />
          </a>
        </div>

        <div
          css={css`
            display: flex;
            gap: 16px;
          `}
        >
          <div
            css={css`
              padding: 16px;
              border-radius: 8px;
              border: 1px dashed ${colors.SOFT_STEEL};
              flex: 1;
            `}
          >
            <Text
              variant='captionXSmall'
              weight='semiBold'
              color={colors.NEUTRAL_50}
            >
              League Info
            </Text>
            <div
              css={css`
                display: flex;
                margin-top: 4px;
              `}
            >
              <div
                css={css`
                  flex: 1;
                  display: flex;
                  flex-direction: column;
                  gap: 4px;
                  margin-top: 4px;
                `}
              >
                {isSportsPay ? (
                  <>
                    <Text
                      variant='heading5'
                      weight='semiBold'
                      color={colors.NEUTRAL_0}
                    >
                      {businessInfo?.company_name}
                    </Text>
                    <AccountInfoRow
                      label='Address'
                      value={
                        businessInfo?.address_street ||
                        businessInfo?.address?.street_1 ||
                        ''
                      }
                    />
                    <AccountInfoRow
                      label='City'
                      value={
                        businessInfo.address_city ||
                        businessInfo?.address?.city ||
                        ''
                      }
                    />
                    <AccountInfoRow
                      label='State/Province'
                      value={
                        businessInfo?.address_state_province ||
                        businessInfo?.address?.province?.name ||
                        ''
                      }
                    />
                    <AccountInfoRow
                      label='Country'
                      value={
                        businessInfo?.address_country ||
                        businessInfo?.address?.country?.name_full ||
                        ''
                      }
                    />
                    <AccountInfoRow
                      label='Postal Code'
                      value={
                        businessInfo?.address_postal_code ||
                        businessInfo?.address?.postal_code ||
                        ''
                      }
                    />
                    <AccountInfoRow
                      label='Phone'
                      value={businessInfo?.phone_number || ''}
                    />
                    <AccountInfoRow
                      label='Email'
                      capitalize={false}
                      value={businessInfo?.email || ''}
                    />
                    {businessInfo.organization?.url && !isSportsPay && (
                      <AccountInfoRow
                        label='Website'
                        capitalize={false}
                        value={businessInfo?.organization?.url || ''}
                      />
                    )}
                  </>
                ) : (
                  <>
                    {businessInfo?.name && (
                      <Text
                        variant='heading4'
                        weight='semiBold'
                        color={colors.NEUTRAL_0}
                      >
                        {businessInfo?.name}
                      </Text>
                    )}
                    {businessInfo?.support_address?.city && (
                      <AccountInfoRow
                        label='City'
                        value={businessInfo?.support_address?.city || ''}
                      />
                    )}
                    {businessInfo?.support_address?.state && (
                      <AccountInfoRow
                        label='State/Province'
                        value={businessInfo.support_address.state || ''}
                      />
                    )}
                    {businessInfo?.support_address?.country && (
                      <AccountInfoRow
                        label='Country'
                        value={businessInfo.support_address.country || ''}
                      />
                    )}
                    {businessInfo?.support_phone && (
                      <AccountInfoRow
                        label='Phone'
                        value={businessInfo.support_phone || ''}
                      />
                    )}
                    {businessInfo?.support_email && (
                      <AccountInfoRow
                        label='Email'
                        capitalize={false}
                        value={businessInfo.support_email || ''}
                      />
                    )}
                    {businessInfo?.url && (
                      <AccountInfoRow
                        label='Website'
                        capitalize={false}
                        value={businessInfo.url || ''}
                      />
                    )}
                  </>
                )}
              </div>
            </div>
          </div>

          <div
            css={css`
              padding: 16px;
              border-radius: 8px;
              border: 1px dashed ${colors.SOFT_STEEL};
              flex: 1;
              margin-top: 4px;
            `}
          >
            <Text
              variant='captionXSmall'
              weight='semiBold'
              color={colors.NEUTRAL_50}
            >
              Account Details
            </Text>

            <div
              css={css`
                display: flex;
                flex-direction: column;
                gap: 8px;
                margin-top: 12px;
              `}
            >
              <AccountInfoRow
                value={
                  typeof provider.accountDetails.account === 'object'
                    ? provider.accountDetails.account?.id || ''
                    : provider.accountNumber || ''
                }
                label='Account ID'
              />
              <AccountInfoRow
                value={
                  typeof provider.accountDetails.account === 'object'
                    ? provider.accountDetails.account?.type
                    : ''
                }
                label='Account Type'
              />
              {isSportsPay ? (
                <AccountInfoRow
                  value={isCanada ? 'CAD' : 'USD'}
                  label='Default Currency'
                  capitalize={false}
                  uppercase
                />
              ) : (
                <AccountInfoRow
                  value={
                    typeof provider.accountDetails.account === 'object'
                      ? provider.accountDetails.account?.default_currency
                      : ''
                  }
                  label='Default Currency'
                  capitalize={false}
                  uppercase
                />
              )}
              <AccountInfoRow
                capitalize={false}
                value={
                  typeof provider.accountDetails.account === 'object'
                    ? provider.accountDetails.account?.email
                    : ''
                }
                label='Account Email'
              />
              <AccountDetailsStatusRow
                label='Details Submitted'
                status={
                  isSportsPay
                    ? 'success'
                    : typeof provider.accountDetails.account === 'object'
                    ? provider.accountDetails.account?.details_submitted
                      ? 'success'
                      : 'error'
                    : 'error'
                }
              />
              <AccountDetailsStatusRow
                label='Charges Enabled'
                status={
                  isSportsPay
                    ? 'success'
                    : typeof provider.accountDetails.account === 'object'
                    ? provider.accountDetails.account?.charges_enabled
                      ? 'success'
                      : 'error'
                    : 'error'
                }
              />
              <AccountDetailsStatusRow
                label='Payouts Enabled'
                status={
                  isSportsPay
                    ? 'success'
                    : typeof provider.accountDetails.account === 'object'
                    ? provider.accountDetails.account?.payouts_enabled
                      ? 'success'
                      : 'error'
                    : 'error'
                }
              />
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div
      css={css`
        padding: 16px;
        background-color: ${colors.SECONDARY_300};
        border: 1px solid
          ${provider.isRecommended ? colors.PRIMARY : colors.SECONDARY_200};
        border-radius: 8px;
        display: flex;
        flex-direction: column;
        gap: 8px;
      `}
    >
      <div
        css={css`
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 8px;
        `}
      >
        {provider.logo}
        <div
          css={css`
            display: flex;
            gap: 8px;
          `}
        >
          {provider.isRecommended && (
            <div
              css={css`
                padding: 8px;
                background-color: rgba(242, 180, 0, 0.1);
                border-radius: 4px;
              `}
            >
              <Text
                variant='captionXSmall'
                weight='semiBold'
                color={colors.PRIMARY}
              >
                RECOMMENDED
              </Text>
            </div>
          )}
          {provider.hasCompetitiveRates && (
            <div
              css={css`
                padding: 8px;
                background-color: rgba(90, 166, 255, 0.1);
                border-radius: 4px;
              `}
            >
              <Text
                variant='captionXSmall'
                weight='semiBold'
                color={colors.INFO_200}
              >
                MOST COMPETITIVE RATES
              </Text>
            </div>
          )}
        </div>
      </div>
      <div
        css={css`
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
        `}
      >
        <div
          css={css`
            display: flex;
            align-items: flex-start;
          `}
        >
          <div>
            <Text variant='heading5' weight='regular' color={colors.WHITE}>
              {provider.name}
            </Text>
            <Text
              variant='paragraphSmall'
              weight='regular'
              color={colors.ATTENDANCE_GRAY}
              style='margin-top: 8px; max-width: 640px;'
            >
              {provider.description}
            </Text>
          </div>
        </div>
      </div>

      <div
        css={css`
          display: flex;
          gap: 16px;
        `}
      >
        {provider.shouldDisplayConnectButton && (
          <button
            css={css`
              height: 40px;
              display: flex;
              padding: 8px 16px;
              justify-content: center;
              align-items: center;
              gap: 8px;
              border-radius: 8px;
              background-color: ${colors.PRIMARY};
              cursor: pointer;
            `}
            onClick={async () => {
              if (provider.connectUrl) {
                const encodedUrl = encodeURI(provider.connectUrl)
                window.open(encodedUrl, '_self')
              } else {
                const { url } = await fetchPaymentProviderUrl(
                  account.uid,
                  provider?.id === 'sportspay' ? '2' : '1'
                )
                if (url) {
                  window.open(url, '_self')
                }
              }
            }}
          >
            <Text
              variant='paragraphSmall'
              weight='medium'
              color={colors.SECONDARY_300}
            >
              Connect to Your {provider.name} Account
            </Text>
            <ExportOutlined
              style={{ fontSize: 16, color: colors.SECONDARY_300 }}
            />
          </button>
        )}
        {provider.contactUrl && (
          <button
            css={css`
              height: 40px;
              display: flex;
              padding: 8px 16px;
              justify-content: center;
              align-items: center;
              gap: 8px;
              border-radius: 8px;
              background-color: transparent;
              border: 1px solid ${colors.WHITE};
              cursor: pointer;
            `}
            onClick={() => window.open(provider.contactUrl, '_blank')}
          >
            <Text variant='paragraphSmall' weight='medium' color={colors.WHITE}>
              Contact Us
            </Text>
            <ExportOutlined style={{ fontSize: 16, color: colors.WHITE }} />
          </button>
        )}
      </div>
    </div>
  )
}
export default PaymentProviders
